import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Pane, TextInputField, Textarea, Button, toaster, Heading, SelectMenu, FileUploader, FileCard, Alert } from 'evergreen-ui';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';
import { MimeType } from 'evergreen-ui';
import './AdminDestination.css';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.mjs',
  import.meta.url
).toString();

function AdminDestination() {
  const [destinations, setDestinations] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('Select a country');
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [docFile, setDocFile] = useState(null);
  const [docRejections, setDocRejections] = useState([]);

  const [formData, setFormData] = useState({
    destination_name: '',
    destination_title1: '',
    description1: '',
    destination_title2: '',
    description2: '',
    country: '',
    image_preview: null,
    image_additional: [],
  });

  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/destinations`);
        if (!response.ok) {
          throw new Error('Failed to fetch destinations');
        }
        const data = await response.json();
        setDestinations(data);
      } catch (error) {
        console.error('Error fetching destinations:', error);
      }
    };

    fetchDestinations();
  }, []);

  // ----------------------------------------------------------------------
  // 1. Download Destination Template
  // ----------------------------------------------------------------------
  const handleDownloadTemplate = async () => {
    const doc = new Document({
      sections: [
        {
          children: [
            new Paragraph({
              children: [new TextRun({ text: "Destination Template", bold: true, size: 28 })],
            }),
            new Paragraph(" "),
            new Paragraph({
              children: [
                new TextRun({ text: "Country: ", bold: true }),
                new TextRun("____________________"),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: "Destination Name: ", bold: true }),
                new TextRun("____________________"),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: "Main Attraction Title: ", bold: true }),
                new TextRun("____________________"),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: "Description of Main Attraction: ", bold: true }),
                new TextRun("Write your description here..."),
              ],
            }),
            new Paragraph(" "),
            new Paragraph({
              children: [
                new TextRun({ text: "Additional Attraction Title: ", bold: true }),
                new TextRun("____________________"),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: "Description of Additional Attraction: ", bold: true }),
                new TextRun("Write your description here..."),
              ],
            }),
          ],
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "Destination_Template.docx";
    link.click();
  };

  // ----------------------------------------------------------------------
  // 2. Handle document upload (PDF or Word)
  // ----------------------------------------------------------------------
  const handleDocUpload = async (files) => {
    const file = files?.[0];
    if (!file) return;

    setDocFile(file);
    setDocRejections([]);
    setExtracting(true);

    try {
      let rawText = "";
      if (file.type === "application/pdf") {
        rawText = await extractTextFromPDF(file);
      } else if (
        file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      ) {
        rawText = await extractTextFromWord(file);
      } else {
        toaster.danger("Please upload a Word (.docx) or PDF file.");
        setDocFile(null);
        return;
      }

      fillFieldsFromExtractedText(rawText);
      toaster.success("Fields auto-filled from the uploaded document!");
    } catch (err) {
      console.error("Extraction error:", err);
      toaster.danger("Failed to read the document.");
    } finally {
      setExtracting(false);
    }
  };

  // ----------------------------------------------------------------------
// 3. PDF text extraction (improved word spacing)
// ----------------------------------------------------------------------
const extractTextFromPDF = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

  let allText = "";

  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const textContent = await page.getTextContent();

    let lastY = null;
    let lastX = null;
    let currentLine = "";

    for (const item of textContent.items) {
      const { str, transform, width } = item;
      const [ , , , , x, y ] = transform;

      // Detect new line based on Y difference
      if (lastY !== null && Math.abs(y - lastY) > 8) {
        allText += currentLine.trim() + "\n";
        currentLine = "";
        lastX = null;
      }

      // Detect word gap based on X difference
      if (lastX !== null) {
        const gap = x - lastX;
        if (gap > width * 0.2) currentLine += " ";
      }

      currentLine += str.trim();
      lastX = x + width;
      lastY = y;
    }

    allText += currentLine.trim() + "\n";
  }

  // Normalize text for parsing
  const normalized = allText
    .replace(/\r/g, "\n")
    .replace(/\u00AD/g, "") // remove soft hyphens
    .replace(/-\s+/g, "") // remove hyphen line breaks
    .replace(/[ \t]+/g, " ") // normalize spaces
    .replace(/\s*:\s*/g, ": ") // normalize colon spacing
    // Fix broken labels
    .replace(/Dest\s*ination\s*Name/gi, "Destination Name")
    .replace(/Main\s*Attraction\s*Title/gi, "Main Attraction Title")
    .replace(/Descr\s*iption\s*of\s*Main\s*Attraction/gi, "Description of Main Attraction")
    .replace(/Add\s*itional\s*Attraction\s*Title/gi, "Additional Attraction Title")
    .replace(/Descr\s*iption\s*of\s*Additional\s*Attraction/gi, "Description of Additional Attraction")
    // Ensure each label starts on a new line
    .replace(/(Country\s*:)/gi, "\n$1")
    .replace(/(Destination\s*Name\s*:)/gi, "\n$1")
    .replace(/(Main\s*Attraction\s*Title\s*:)/gi, "\n$1")
    .replace(/(Description\s*of\s*Main\s*Attraction\s*:)/gi, "\n$1")
    .replace(/(Additional\s*Attraction\s*Title\s*:)/gi, "\n$1")
    .replace(/(Description\s*of\s*Additional\s*Attraction\s*:)/gi, "\n$1")
    .replace(/\n{2,}/g, "\n")
    .trim();

  console.log("✅ Normalized PDF text:", normalized);
  return normalized;
};

// ----------------------------------------------------------------------
// 4. Word extraction using Mammoth (improved spacing)
// ----------------------------------------------------------------------
const extractTextFromWord = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  
  let text = result.value
    .replace(/\r/g, "\n")
    .replace(/\u00AD/g, "") // soft hyphen
    .replace(/-\s*\n/g, "") // join split lines
    .replace(/[ \t]+/g, " ") // normalize spaces
    .replace(/\s*:\s*/g, ": ") // normalize colon spacing
    // Fix broken labels
    .replace(/Dest\s*ination\s*Name/gi, "Destination Name")
    .replace(/Main\s*Attraction\s*Title/gi, "Main Attraction Title")
    .replace(/Descr\s*iption\s*of\s*Main\s*Attraction/gi, "Description of Main Attraction")
    .replace(/Add\s*itional\s*Attraction\s*Title/gi, "Additional Attraction Title")
    .replace(/Descr\s*iption\s*of\s*Additional\s*Attraction/gi, "Description of Additional Attraction")
    // Ensure each label starts on a new line
    .replace(/(Country\s*:)/gi, "\n$1")
    .replace(/(Destination\s*Name\s*:)/gi, "\n$1")
    .replace(/(Main\s*Attraction\s*Title\s*:)/gi, "\n$1")
    .replace(/(Description\s*of\s*Main\s*Attraction\s*:)/gi, "\n$1")
    .replace(/(Additional\s*Attraction\s*Title\s*:)/gi, "\n$1")
    .replace(/(Description\s*of\s*Additional\s*Attraction\s*:)/gi, "\n$1")
    .replace(/\n{2,}/g, "\n")
    .trim();

  console.log("✅ Normalized Word text:", text);
  return text;
};

// ----------------------------------------------------------------------
// 5. Autofill logic — unchanged but works better now
// ----------------------------------------------------------------------
const fillFieldsFromExtractedText = (text) => {
  const lines = text.split("\n").map(l => l.trim()).filter(Boolean);

  let country = "", destination_name = "", destination_title1 = "", description1 = "", destination_title2 = "", description2 = "";
  let currentField = null;
  let buffer = [];

  for (const line of lines) {
    if (line.startsWith("Country:")) {
      if (currentField?.startsWith("description")) buffer = [];
      currentField = "country";
      country = line.replace(/^Country:\s*/, "").trim();
    } 
    else if (line.startsWith("Destination Name:")) {
      if (currentField?.startsWith("description")) buffer = [];
      currentField = "destination_name";
      destination_name = line.replace(/^Destination Name:\s*/, "").trim();
    } 
    else if (line.startsWith("Main Attraction Title:")) {
      if (currentField?.startsWith("description")) buffer = [];
      currentField = "destination_title1";
      destination_title1 = line.replace(/^Main Attraction Title:\s*/, "").trim();
    } 
    else if (line.startsWith("Description of Main Attraction:")) {
      if (currentField === "description1") description1 = buffer.join(" ");
      buffer = [];
      currentField = "description1";
      buffer.push(line.replace(/^Description of Main Attraction:\s*/, "").trim());
    } 
    else if (line.startsWith("Additional Attraction Title:")) {
      if (currentField === "description1") description1 = buffer.join(" ");
      buffer = [];
      currentField = "destination_title2";
      destination_title2 = line.replace(/^Additional Attraction Title:\s*/, "").trim();
    } 
    else if (line.startsWith("Description of Additional Attraction:")) {
      if (currentField === "description1") description1 = buffer.join(" ");
      buffer = [];
      currentField = "description2";
      buffer.push(line.replace(/^Description of Additional Attraction:\s*/, "").trim());
    } 
    else if (currentField === "description1" || currentField === "description2") {
      buffer.push(line);
    }
  }

  if (currentField === "description1") description1 = buffer.join(" ").trim();
  else if (currentField === "description2") description2 = buffer.join(" ").trim();

  console.log("✅ Extracted fields:", { country, destination_name, destination_title1, description1, destination_title2, description2 });

  setFormData(prev => ({
    ...prev,
    country: country || prev.country,
    destination_name: destination_name || prev.destination_name,
    destination_title1: destination_title1 || prev.destination_title1,
    description1: description1 || prev.description1,
    destination_title2: destination_title2 || prev.destination_title2,
    description2: description2 || prev.description2,
  }));

  if (country) setSelectedCountry(country);
};

  // ----------------------------------------------------------------------
  // 6. Image Preview (Single File Upload)
  // ----------------------------------------------------------------------
  const FileUploaderSingleUploadDemo = () => {
    const [files, setFiles] = useState(formData.image_preview ? [formData.image_preview] : []);
    const [fileRejections, setFileRejections] = useState([]);
    const handleChange = useCallback((files) => {
      if (files[0]) {
        console.log("Preview Image Selected:", files[0]); // Debug log
        setFiles([files[0]]);
        setFormData((prev) => ({ ...prev, image_preview: files[0] }));
      }
    }, []);
    const handleRejected = useCallback((fileRejections) => {
      setFileRejections([fileRejections[0]]);
      toaster.danger("Invalid image. Max 10 MB, one file only.");
    }, []);
    const handleRemove = useCallback(() => {
      setFiles([]);
      setFileRejections([]);
      setFormData((prev) => ({ ...prev, image_preview: null }));
    }, []);

    return (
      <Pane maxWidth={654}>
        <FileUploader
          label="Upload Preview Image"
          description="Max 10 MB, one image (JPEG, PNG)"
          maxSizeInBytes={10 * 1024 ** 2}
          maxFiles={1}
          onChange={handleChange}
          onRejected={handleRejected}
          accept={[MimeType.jpeg, MimeType.png]}
          renderFile={(file) => {
            const { name, size, type } = file;
            const fileRejection = fileRejections.find((fileRejection) => fileRejection.file === file);
            const { message } = fileRejection || {};
            return (
              <FileCard
                key={name}
                isInvalid={fileRejection != null}
                name={name}
                onRemove={handleRemove}
                sizeInBytes={size}
                type={type}
                validationMessage={message}
              />
            );
          }}
          values={files}
        />
      </Pane>
    );
  };

  // ----------------------------------------------------------------------
  // 7. Additional Images (Multiple File Upload)
  // ----------------------------------------------------------------------
  const FileUploaderMultipleUploadDemo = () => {
    const acceptedMimeTypes = useMemo(() => [MimeType.jpeg, MimeType.png], []);
    const maxFiles = 3;
    const maxSizeInBytes = 10 * 1024 ** 2; // 10 MB
    const [files, setFiles] = useState(formData.image_additional);
    const [fileRejections, setFileRejections] = useState([]);
    const values = useMemo(
      () => [...files, ...fileRejections.map((fileRejection) => fileRejection.file)],
      [files, fileRejections]
    );

    const handleRemove = useCallback(
      (file) => {
        const updatedFiles = files.filter((existingFile) => existingFile !== file);
        const updatedFileRejections = fileRejections.filter((fileRejection) => fileRejection.file !== file);

        const { accepted, rejected } = rebaseFiles(
          [...updatedFiles, ...updatedFileRejections.map((fileRejection) => fileRejection.file)],
          { acceptedMimeTypes, maxFiles, maxSizeInBytes }
        );

        setFiles(accepted);
        setFileRejections(rejected);
        setFormData((prev) => ({ ...prev, image_additional: accepted }));
      },
      [acceptedMimeTypes, maxFiles, maxSizeInBytes, files, fileRejections]
    );

    const fileCountOverLimit = files.length + fileRejections.length - maxFiles;
    const fileCountError = `You can upload up to 3 files. Please remove ${fileCountOverLimit} ${
      fileCountOverLimit === 1 ? 'file' : 'files'
    }.`;

    return (
      <Pane maxWidth={654}>
        <FileUploader
          acceptedMimeTypes={acceptedMimeTypes}
          label="Upload Additional Images"
          description="Up to 3 images, max 10 MB each (JPEG, PNG)"
          disabled={files.length + fileRejections.length >= maxFiles}
          maxSizeInBytes={maxSizeInBytes}
          maxFiles={maxFiles}
          onAccepted={(acceptedFiles) => {
            console.log("Additional Images Selected:", acceptedFiles); // Debug log
            setFiles(acceptedFiles);
            setFormData((prev) => ({ ...prev, image_additional: acceptedFiles }));
          }}
          onRejected={(rejectedFiles) => {
            setFileRejections(rejectedFiles);
            toaster.danger("Invalid images. Max 10 MB each, up to 3 JPEG/PNG files.");
          }}
          renderFile={(file, index) => {
            const { name, size, type } = file;
            const renderFileCountError = index === 0 && fileCountOverLimit > 0;
            const fileRejection = fileRejections.find(
              (fileRejection) => fileRejection.file === file && fileRejection.reason !== "OverFileLimit"
            );
            const { message } = fileRejection || {};

            return (
              <React.Fragment key={`${file.name}-${index}`}>
                {renderFileCountError && <Alert intent="danger" marginBottom={16} title={fileCountError} />}
                <FileCard
                  isInvalid={fileRejection != null}
                  name={name}
                  onRemove={() => handleRemove(file)}
                  sizeInBytes={size}
                  type={type}
                  validationMessage={message}
                />
              </React.Fragment>
            );
          }}
          values={values}
        />
      </Pane>
    );
  };

  // Helper function for rebaseFiles (from Evergreen UI)
  const rebaseFiles = (files, { acceptedMimeTypes, maxFiles, maxSizeInBytes }) => {
    const accepted = [];
    const rejected = [];

    files.forEach((file) => {
      if (
        accepted.length < maxFiles &&
        file.size <= maxSizeInBytes &&
        acceptedMimeTypes.includes(file.type)
      ) {
        accepted.push(file);
      } else {
        const reason =
          file.size > maxSizeInBytes
            ? "FileSize"
            : !acceptedMimeTypes.includes(file.type)
            ? "FileType"
            : "OverFileLimit";
        rejected.push({ file, reason });
      }
    });

    return { accepted, rejected };
  };

  // ----------------------------------------------------------------------
  // 8. Handle form submission
  // ----------------------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (
      !formData.country ||
      !formData.destination_name ||
      !formData.destination_title1 ||
      !formData.description1 ||
      !formData.destination_title2 ||
      !formData.description2 ||
      !formData.image_preview ||
      formData.image_additional.length < 3
    ) {
      toaster.danger("All fields and images are required.");
      setLoading(false);
      return;
    }

    const uploadData = new FormData();
    uploadData.append('destination_name', formData.destination_name);
    uploadData.append('destination_title1', formData.destination_title1);
    uploadData.append('description1', formData.description1);
    uploadData.append('destination_title2', formData.destination_title2);
    uploadData.append('description2', formData.description2);
    uploadData.append('country', formData.country);
    uploadData.append('image_preview', formData.image_preview);
    formData.image_additional.forEach((file, index) => {
      uploadData.append(`image${index + 1}`, file);
    });

    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/destinations`, {
        method: 'POST',
        body: uploadData
      });
      if (!response.ok) {
        throw new Error('Failed to upload destination');
      }
      const newDestination = await response.json();
      setDestinations([...destinations, newDestination]);

      toaster.success('Destination uploaded successfully!');
      setFormData({
        destination_name: '',
        destination_title1: '',
        description1: '',
        destination_title2: '',
        description2: '',
        country: '',
        image_preview: null,
        image_additional: [],
      });
      setSelectedCountry('Select a country');
      setDocFile(null);
      setDocRejections([]);
    } catch (error) {
      console.error('Error uploading destination:', error);
      toaster.danger('Failed to upload destination');
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------------------------------------------------
  // 9. Render UI
  // ----------------------------------------------------------------------
  return (
    <Pane className="dest-container">
      <Heading size={700} marginBottom={24} className="dest-title">
        Upload New Destination
      </Heading>
      <form className="upload-form" onSubmit={handleSubmit}>
        <div className="columns-container">
          {/* LEFT COLUMN */}
          <div className="left-column">
            <SelectMenu
              title="Select Country"
              options={['Kenya', 'Uganda', 'Tanzania'].map((label) => ({ label, value: label }))}
              selected={selectedCountry}
              onSelect={(item) => {
                setSelectedCountry(item.value);
                setFormData({ ...formData, country: item.value });
              }}
            >
              <Button className="select-menu-button">
                {selectedCountry || 'Select a country (e.g., Kenya)'}
              </Button>
            </SelectMenu>

            <TextInputField
              label="Destination Name"
              name="destination_name"
              value={formData.destination_name}
              onChange={(e) => setFormData({ ...formData, destination_name: e.target.value })}
              placeholder="Enter the full destination name (e.g., 'Mount Kilimanjaro National Park')"
              required
            />

            <TextInputField
              label="Main Attraction Title"
              name="destination_title1"
              value={formData.destination_title1}
              onChange={(e) => setFormData({ ...formData, destination_title1: e.target.value })}
              placeholder="Enter the title for the first attraction (e.g., 'Hiking Trails')"
              required
            />

            <Textarea
              label="Description of Main Attraction"
              name="description1"
              value={formData.description1}
              onChange={(e) => setFormData({ ...formData, description1: e.target.value })}
              placeholder="Provide a detailed description of the first attraction..."
              height={150}
              required
            />

            <TextInputField
              label="Additional Attraction Title"
              name="destination_title2"
              value={formData.destination_title2}
              onChange={(e) => setFormData({ ...formData, destination_title2: e.target.value })}
              placeholder="Enter the title for the second attraction"
              required
            />

            <Textarea
              label="Description of Additional Attraction"
              name="description2"
              value={formData.description2}
              onChange={(e) => setFormData({ ...formData, description2: e.target.value })}
              placeholder="Provide a detailed description of the second attraction..."
              height={150}
              required
            />

            <div className="image-picker">
              <label>Preview Image</label>
              <FileUploaderSingleUploadDemo />
            </div>

            <div className="image-picker">
              <label>Additional Images (up to 3)</label>
              <FileUploaderMultipleUploadDemo />
            </div>
          </div>

          {/* DIVIDER */}
          <div className="divider" />

          {/* RIGHT COLUMN */}
          <div className="right-column">
            <div className="file-section">
              <h4>Auto-fill from Template</h4>
              <p className="template-desc">
                Download the Word template, fill it, then upload it here.
              </p>

              <Button
                appearance="minimal"
                intent="success"
                onClick={handleDownloadTemplate}
              >
                ⬇️ Download Template
              </Button>

              <hr className="template-divider" />

              <FileUploader
                label="Upload Filled Template"
                description="Word (.docx) or PDF, max 50 MB"
                maxSizeInBytes={50 * 1024 ** 2}
                maxFiles={1}
                onChange={handleDocUpload}
                onRejected={setDocRejections}
                renderFile={(file) => (
                  <FileCard
                    key={file.name}
                    name={file.name}
                    sizeInBytes={file.size}
                    type={file.type}
                    onRemove={() => setDocFile(null)}
                  />
                )}
                values={docFile ? [docFile] : []}
              />

              {docRejections.length > 0 && (
                <ul className="file-rejections">
                  {docRejections.map(({ file, errors }) => (
                    <li key={file.name} className="rejection-item">
                      ❌ <strong>{file.name}</strong> —{" "}
                      {errors.map((e) => e.message).join(", ")}
                    </li>
                  ))}
                </ul>
              )}

              {extracting && <p className="loading-text">Extracting text…</p>}
            </div>
          </div>
        </div>

        <Button
          appearance="primary"
          intent="success"
          height={44}
          iconBefore="add"
          type="submit"
          isLoading={loading}
        >
          {loading ? "Uploading…" : "Upload Destination"}
        </Button>
      </form>
    </Pane>
  );
}

export default AdminDestination;